import React from 'react';
import { Button, Card } from '../components/redesign/ui';
import {
  Plus, DownloadSimple, UploadSimple, MagnifyingGlass, PencilSimple,
  FloppyDisk, Trash, Package, CheckCircle, XCircle, CaretDown,
  MicrosoftExcelLogo, Tag, Clock,
} from '@phosphor-icons/react';

// Column definitions must match the backend normalise() shape exactly.
// Rendered order + widths — kept identical to the pre-redesign layout.
const COLUMNS = [
  { key: 'sap_item_code',    label: 'SAP Item Code',       width: 'w-32'  },
  { key: 'sap_bundle_code',  label: 'SAP Bundle Code',     width: 'w-32'  },
  { key: 'product_name',     label: 'Product Name (EN)',   width: 'w-48'  },
  { key: 'product_name_ar',  label: 'Product Name (AR)',   width: 'w-40'  },
  { key: 'product_family',   label: 'Product Family',      width: 'w-36'  },
  { key: 'size',             label: 'Size',                width: 'w-20'  },
  { key: 'pack_type',        label: 'Pack Type',           width: 'w-36'  },
  { key: 'pack_qty_units',   label: 'Pack Qty (units)',    width: 'w-28'  },
  { key: 'bundle_qty_packs', label: 'Bundle Qty (packs)',  width: 'w-28'  },
  { key: 'original_price',   label: 'Original Price (KD)', width: 'w-32'  },
  { key: 'promo_price',      label: 'Promo Price (KD)',    width: 'w-28'  },
  { key: 'offer_type',       label: 'Offer Type',          width: 'w-28'  },
  { key: 'free_items',       label: 'Free Items',          width: 'w-24'  },
  { key: 'start_date',       label: 'Start Date',          width: 'w-44'  },
  { key: 'end_date',         label: 'End Date',            width: 'w-44'  },
  { key: 'notes',            label: 'Notes',               width: 'w-48'  },
  { key: 'status',           label: 'Status',              width: 'w-28'  },
];

const STATUS_OPTIONS = ['active', 'expired', 'inactive'];

// Editable status select background + text tint (dot dropped on interactive
// variant per spec; token-driven).
const STATUS_STYLES = {
  active:   'bg-status-resolved/12 text-status-resolved',
  expired:  'bg-status-escalated/12 text-status-escalated',
  inactive: 'bg-paper-muted text-ink-muted',
};

// Stat card — tokenized, phosphor icon in a paper-muted square.
function StatCard({ label, value, icon: Icon, tone = 'ink' }) {
  const iconCls = {
    ink:              'bg-paper-muted text-ink',
    resolved:         'bg-status-resolved/12 text-status-resolved',
    escalated:        'bg-status-escalated/12 text-status-escalated',
    'pending-finance':'bg-status-pending-finance/12 text-status-pending-finance',
    'under-process':  'bg-status-under-process/12 text-status-under-process',
  }[tone] || 'bg-paper-muted text-ink';
  return (
    <Card className="flex items-center gap-4 p-5">
      <div className={`flex h-11 w-11 flex-none items-center justify-center rounded-field ${iconCls}`}>
        <Icon size={20} weight="regular" />
      </div>
      <div>
        <p className="font-serif text-2xl text-ink">{value}</p>
        <p className="mt-0.5 text-xs text-ink-muted">{label}</p>
      </div>
    </Card>
  );
}

// Inline editable cell — text/number/date input or a status <select>.
function EditableCell({ value, onChange, type = 'text', col, statusLabels }) {
  if (col.key === 'status') {
    return (
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`cursor-pointer appearance-none rounded-chip border-0 px-2.5 py-1 pr-5 text-xs font-semibold outline-none ${STATUS_STYLES[value] || STATUS_STYLES.inactive}`}>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{statusLabels[s]}</option>
          ))}
        </select>
        <CaretDown size={12} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-ink-faint" />
      </div>
    );
  }
  // product_name_ar: always allow Arabic text with directional detection.
  const extraProps = col.key === 'product_name_ar' ? { dir: 'auto' } : {};
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-w-0 rounded border-0 bg-transparent px-1 py-0.5 text-sm text-ink outline-none focus:bg-status-under-process/8 focus:ring-1 focus:ring-status-under-process/40"
      {...extraProps}
    />
  );
}

// ── App perks editor sub-section ────────────────────────────────────────────
// Reads / writes app_settings.app_perks. Edits take effect on the next email
// the CS agent handles (agent re-fetches /api/offers/active per email).
function AppPerksEditor({ appPerks, saving, savedAt, onLinkChange, onItemChange, onSave }) {
  const link = appPerks?.app_link || '';
  const items = appPerks?.items || [];
  return (
    <div className="rounded-card border border-paper-border bg-paper-panel shadow-soft p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-base text-ink">App Perks</h3>
          <p className="mt-0.5 text-[12px] text-ink-muted">Perks the email agent may promote. Changes take effect on the next email handled.</p>
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          className="rounded-field bg-ink px-4 py-2 text-xs font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
      <div className="mb-4">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-faint">App link</label>
        <input
          type="text"
          value={link}
          onChange={(e) => onLinkChange(e.target.value)}
          placeholder="links.abraajwater.com"
          className="w-full rounded-field border border-paper-border2 bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink"
        />
      </div>
      <div className="space-y-3">
        {items.length === 0 && <div className="text-[13px] text-ink-faint italic">No perks defined.</div>}
        {items.map((it, i) => (
          <div key={i} className="rounded-field border border-paper-border2 bg-paper p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <input
                type="text"
                value={it.title || ''}
                onChange={(e) => onItemChange(i, 'title', e.target.value)}
                placeholder="Perk title"
                className="flex-1 rounded-field border border-paper-border2 bg-paper-panel px-3 py-1.5 text-sm font-semibold text-ink outline-none focus:border-ink"
              />
              <label className="flex flex-none cursor-pointer items-center gap-2 text-[12px] text-ink-muted">
                <input
                  type="checkbox"
                  checked={it.enabled !== false}
                  onChange={(e) => onItemChange(i, 'enabled', e.target.checked)}
                />
                <span>{it.enabled !== false ? 'Enabled' : 'Off'}</span>
              </label>
              <span className="rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] font-mono text-ink-faint">{it.key}</span>
            </div>
            <textarea
              rows={3}
              value={it.message || ''}
              onChange={(e) => onItemChange(i, 'message', e.target.value)}
              placeholder="Message the agent will quote verbatim…"
              className="w-full resize-none rounded-field border border-paper-border2 bg-paper-panel px-3 py-2 text-[13px] leading-relaxed text-ink outline-none focus:border-ink"
            />
          </div>
        ))}
      </div>
      {savedAt && <div className="mt-3 text-[11px] text-status-resolved">Saved {savedAt.toLocaleTimeString()}.</div>}
    </div>
  );
}

export default function OffersPromoPageView({
  // data
  rows,
  displayRows,
  totalOffers,
  activeOffers,
  expiredCount,
  expiringSoonCount,
  filters,
  filter,
  search,
  delivery,
  editingDelivery,
  deliveryDraft,
  dragging,
  syncing,
  isRTL,
  t,
  statusLabels,
  // refs
  fileInputRef,
  // handlers
  onAddRow,
  onExportExcel,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onSearchChange,
  onFilterChange,
  onStartEditDelivery,
  onSaveDelivery,
  onDeliveryDraftChange,
  appPerks,
  appPerksSaving,
  appPerksSavedAt,
  onAppPerkLinkChange,
  onAppPerkItemChange,
  onSaveAppPerks,
  onUpdateCell,
  onDeleteRow,
}) {
  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="p-1">
      <div className="flex flex-col gap-6">
        {/* ── Header ── */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <Tag size={18} weight="regular" className="text-status-under-process" />
              <h1 className="font-serif text-2xl text-ink">{t('offersTitle')}</h1>
            </div>
            <p className="text-[13px] text-ink-muted">{t('offersSubtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {syncing && (
              <span className="flex items-center gap-1.5 text-xs text-ink-faint">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-status-under-process" />
                Syncing…
              </span>
            )}
            <Button variant="secondary" onClick={onExportExcel}>
              <DownloadSimple size={14} weight="bold" />{t('exportExcel')}
            </Button>
            <Button variant="primary" onClick={onAddRow}>
              <Plus size={14} weight="bold" />{t('addOffer')}
            </Button>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label={t('totalOffers')}  value={totalOffers}       icon={Package}      tone="ink" />
          <StatCard label={t('activeOffers')} value={activeOffers}      icon={CheckCircle}  tone="resolved" />
          <StatCard label="Expiring Soon"     value={expiringSoonCount} icon={Clock}        tone="pending-finance" />
          <StatCard label={t('expired')}      value={expiredCount}      icon={XCircle}      tone="escalated" />
        </div>

        {/* ── Upload zone ── */}
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`rounded-card border-2 border-dashed p-8 text-center transition-colors ${
            dragging
              ? 'border-status-under-process bg-status-under-process/8'
              : 'border-paper-border2 bg-paper-panel hover:border-status-under-process/40'
          }`}>
          <MicrosoftExcelLogo size={40} weight="regular" className="mx-auto mb-3 text-ink-faint" />
          <p className="mb-1 text-sm font-medium text-ink">{t('uploadTitle')}</p>
          <p className="mb-4 text-xs text-ink-faint">{t('uploadSubtitle')}</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="mx-auto flex items-center gap-2 rounded-field border border-paper-border2 bg-paper px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-paper-muted">
            <UploadSimple size={14} weight="bold" />{t('chooseFile')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={onFileChange}
          />
        </div>

        {/* ── Delivery schedule ── */}
        <Card className="p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-base text-ink">{t('deliveryTitle')}</h3>
            {!editingDelivery ? (
              <button
                onClick={onStartEditDelivery}
                className="flex items-center gap-1.5 text-xs font-medium text-status-under-process hover:underline">
                <PencilSimple size={13} />{t('edit')}
              </button>
            ) : (
              <button
                onClick={onSaveDelivery}
                className="flex items-center gap-1.5 text-xs font-medium text-status-resolved hover:underline">
                <FloppyDisk size={13} weight="fill" />{t('save')}
              </button>
            )}
          </div>
          {editingDelivery ? (
            <textarea
              value={deliveryDraft}
              onChange={(e) => onDeliveryDraftChange(e.target.value)}
              rows={5}
              className="w-full resize-none rounded-field border border-paper-border2 bg-paper p-3 text-sm leading-relaxed text-ink outline-none focus:border-ink"
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          ) : (
            <p className="whitespace-pre-line text-sm leading-relaxed text-ink-muted">{delivery}</p>
          )}
        </Card>

        {/* ── App perks editor ── */}
        <AppPerksEditor
          appPerks={appPerks}
          saving={appPerksSaving}
          savedAt={appPerksSavedAt}
          onLinkChange={onAppPerkLinkChange}
          onItemChange={onAppPerkItemChange}
          onSave={onSaveAppPerks}
        />

        {/* ── Search + filter bar ── */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlass size={16}
              className={`absolute top-1/2 -translate-y-1/2 text-ink-faint ${isRTL ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className={`w-full rounded-field border border-paper-border2 bg-paper py-2 text-sm text-ink outline-none focus:border-ink ${isRTL ? 'pl-4 pr-9' : 'pl-9 pr-4'}`}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {filters.map((f) => {
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => onFilterChange(f.key)}
                  className={`rounded-field px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? 'bg-ink text-paper'
                      : 'border border-paper-border2 bg-paper text-ink-muted hover:bg-paper-muted'
                  }`}>
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Table ── */}
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="border-b border-paper-border bg-paper-muted/40">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-ink-faint ${col.width} ${isRTL ? 'text-right' : 'text-left'}`}>
                      {col.label}
                    </th>
                  ))}
                  <th className="w-12 px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="py-16 text-center">
                      <MicrosoftExcelLogo size={40} className="mx-auto mb-3 text-ink-faint opacity-40" />
                      <p className="text-sm font-medium text-ink-muted">{t('emptyTitle')}</p>
                      <p className="mt-1 text-xs text-ink-faint">{t('emptySubtitle')}</p>
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row) => (
                    <tr key={row.id} className="group border-b border-paper-border transition-colors hover:bg-paper-muted/50">
                      {COLUMNS.map((col) => (
                        <td key={col.key} className={`px-3 py-2 ${col.width}`}>
                          <EditableCell
                            value={row[col.key]}
                            col={col}
                            statusLabels={statusLabels}
                            onChange={(val) => onUpdateCell(row.id, col.key, val)}
                            type={
                              ['start_date', 'end_date'].includes(col.key) ? 'datetime-local' :
                              ['original_price', 'promo_price', 'pack_qty_units', 'bundle_qty_packs'].includes(col.key) ? 'number' :
                              'text'
                            }
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => onDeleteRow(row.id)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-field text-ink-faint opacity-0 transition-all hover:bg-status-escalated/8 hover:text-status-escalated group-hover:opacity-100">
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer hint */}
          {displayRows.length > 0 && (
            <div className="border-t border-paper-border bg-paper-muted/40 px-4 py-2.5">
              <p className="text-xs italic text-ink-faint">{t('footerHint')}</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
